import React, { useEffect, useState } from "react";
import { Check, Loader2, Trash2, UserRound, ArrowRightLeft, Merge, Pencil } from "lucide-react";
import AccountEditDialog from "./AccountEditDialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { idb } from "@/lib/offlineDb";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

// Accounts sharing the signed-in e-mail (e.g. Google + password login).
export function useMyAccounts(user) {
  const [accounts, setAccounts] = useState([]);
  const load = () => api.get("/auth/my-accounts").then((r) => setAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { if (user && navigator.onLine) load(); }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return [accounts, load];
}

const fmt = (s) => { try { return new Date(s).toLocaleDateString("de-DE"); } catch (_) { return ""; } };
const login = (a) => (a.auth_provider === "google" ? "Google-Anmeldung" : "Passwort-Anmeldung");

export default function AccountsDialog({ open, onOpenChange, accounts, reload, isAdmin, chooser }) {
  const [busy, setBusy] = useState(null);
  const [del, setDel] = useState(null);
  const [merge, setMerge] = useState(null);
  const [edit, setEdit] = useState(null);
  const current = accounts.find((a) => a.is_current);

  const doMerge = async () => {
    const a = merge;
    setMerge(null);
    setBusy(a.user_id);
    try {
      const { data } = await api.post(`/auth/my-accounts/${a.user_id}/merge`);
      toast.success(`${data.moved_projects} Projekt${data.moved_projects === 1 ? "" : "e"} übernommen – Konten zusammengeführt`);
      try { await idb.clear("cache"); } catch (_) {}
      reload();
      window.dispatchEvent(new Event("projects-changed"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };

  const switchTo = async (a) => {
    setBusy(a.user_id);
    try {
      await api.post(`/auth/my-accounts/${a.user_id}/switch`);
      try { await idb.clear("cache"); } catch (_) {}
      window.location.assign("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(null); }
  };
  const remove = async () => {
    const a = del;
    setDel(null);
    setBusy(a.user_id);
    try {
      await api.delete(`/auth/my-accounts/${a.user_id}`);
      toast.success("Konto und zugehörige Daten gelöscht");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="accounts-dialog">
          <DialogHeader>
            <DialogTitle>{chooser ? "Mit welchem Konto arbeiten?" : "Meine Konten"}</DialogTitle>
            <DialogDescription>Zu Ihrer E-Mail-Adresse gibt es {accounts.length} {accounts.length === 1 ? "Konto" : "Konten"}. Wählen Sie eines aus{isAdmin ? ", führen Sie ein anderes Konto mit dem aktiven zusammen (Projekte werden übernommen) oder löschen Sie es samt Daten" : ""}.</DialogDescription>
          </DialogHeader>
          <ul className="space-y-2">
            {accounts.map((a) => (
              <li key={a.user_id} data-testid={`account-${a.user_id}`}
                className={`flex items-center gap-3 rounded-lg border px-3 py-3 ${a.is_current ? "border-primary/50 bg-orange-50/60" : "border-slate-200"}`}>
                <UserRound className="h-5 w-5 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{a.name || a.email}</p>
                  <p className="text-xs text-slate-500">{login(a)} · {a.projects} Projekt{a.projects === 1 ? "" : "e"} · seit {fmt(a.created_at)}{a.own_company ? "" : " · Mitarbeiter"}{a.active ? "" : " · deaktiviert"}</p>
                </div>
                {a.is_current ? (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary" data-testid={`account-current-${a.user_id}`}><Check className="h-3.5 w-3.5" />Aktiv</span>
                    <Button size="sm" variant="ghost" className="px-2" onClick={() => setEdit(a)} title="Konto bearbeiten" data-testid={`account-edit-${a.user_id}`}><Pencil className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="px-2" disabled={!!busy} onClick={() => setEdit(a)} title="Konto bearbeiten" data-testid={`account-edit-${a.user_id}`}><Pencil className="h-4 w-4" /></Button>
                    )}
                    <Button size="sm" variant="outline" disabled={!!busy || !a.active} onClick={() => switchTo(a)} data-testid={`account-switch-${a.user_id}`}>
                      {busy === a.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ArrowRightLeft className="h-3.5 w-3.5 mr-1" />Wechseln</>}
                    </Button>
                    {isAdmin && a.own_company && (
                      <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setMerge(a)} title="Projekte in das aktive Konto übernehmen"
                        className="px-2" data-testid={`account-merge-${a.user_id}`}>
                        <Merge className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Zusammenführen</span>
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setDel(a)} title="Konto löschen"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 px-2" data-testid={`account-delete-${a.user_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {chooser && <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="accounts-keep-current">Mit dem aktiven Konto weiterarbeiten</Button>}
        </DialogContent>
      </Dialog>
      <AccountEditDialog account={edit} onClose={() => setEdit(null)} onSaved={reload} />
      <AlertDialog open={!!merge} onOpenChange={(v) => !v && setMerge(null)}>
        <AlertDialogContent data-testid="account-merge-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Konten zusammenführen?</AlertDialogTitle>
            <AlertDialogDescription>
              {merge?.projects === 1 ? "Das Projekt" : `Alle ${merge?.projects} Projekte`} aus „{merge?.name}“ ({merge && login(merge)}) {merge?.projects === 1 ? "wird" : "werden"} mit Plänen, Mängeln, Tickets und Fotos in das aktive Konto „{current?.name}“ übernommen.
              Ihm zugewiesene Aufgaben gehen an das aktive Konto. Danach wird das leere Konto „{merge?.name}“ entfernt – es gehen keine Daten verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="account-merge-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={doMerge} data-testid="account-merge-confirm-btn">Zusammenführen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent data-testid="account-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Konto endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Das Konto „{del?.name}“ ({del && login(del)}) wird mit {del?.projects === 1 ? "seinem Projekt" : `allen ${del?.projects} Projekten`} und allen Plänen, Mängeln, Tickets und Fotos gelöscht. Das lässt sich nicht rückgängig machen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="account-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="account-delete-confirm-btn">Endgültig löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
